import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Search,
  CreditCard,
  RefreshCw,
  AlertTriangle,
  Filter,
  ArrowUpDown,
  Activity,
  BarChart2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useConsumerSearch } from '../hooks/useReports';
import { useCardStatusHistory } from '../hooks/useCardStatus';
import { formatDate } from '../utils/formatters';

const STATUS_COLORS = {
  Ordered: 'bg-yellow-100 text-yellow-900',
  Created: 'bg-yellow-50 text-yellow-700',
  Shipped: 'bg-indigo-100 text-indigo-800',
  Active: 'bg-green-100 text-green-700',
  Purchase: 'bg-green-200 text-green-900',
  Return: 'bg-blue-100 text-blue-800',
  Declined: 'bg-gray-200 text-gray-900',
  Suspended: 'bg-yellow-100 text-yellow-800',
  'Potential fraud': 'bg-red-100 text-red-800',
  Replaced: 'bg-orange-100 text-orange-800',
  Hibernating: 'bg-gray-100 text-gray-700',
  'Reported as lost or stolen': 'bg-red-100 text-red-800',
};

const STATUS_DOT_COLORS = {
  Ordered: '#a16207',
  Created: '#ca8a04',
  Shipped: '#6366f1',
  Active: '#4ade80',
  Purchase: '#15803d',
  Return: '#2563eb',
  Declined: '#111827',
  Suspended: '#eab308',
  'Potential fraud': '#ef4444',
  Replaced: '#f97316',
  Hibernating: '#9ca3af',
  'Reported as lost or stolen': '#dc2626',
};

const STATUS_LIFECYCLE_ORDER = [
  'Ordered',
  'Created',
  'Shipped',
  'Active',
  'Purchase',
  'Return',
  'Declined',
  'Suspended',
  'Potential fraud',
  'Hibernating',
  'Replaced',
  'Reported as lost or stolen',
];

const AUTO_COLORS = ['#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#a855f7'];
let autoColorIdx = 0;
function getDotColor(status) {
  if (STATUS_DOT_COLORS[status]) return STATUS_DOT_COLORS[status];
  const color = AUTO_COLORS[autoColorIdx % AUTO_COLORS.length];
  STATUS_DOT_COLORS[status] = color;
  autoColorIdx++;
  return color;
}

const UTIL_STATUSES = ['Purchase', 'Return', 'Declined', 'Potential fraud'];

// Shared SVG layout constants — both charts use identical values so
// the card-label column and plot area align visually.
const LABEL_W  = 220;
const TOP_PAD  = 8;
const AXIS_H   = 30;

// Lifecycle chart lane
const LANE_H    = 40;
const SEG_V_PAD = 7;

// Utilization chart lane (taller for bar headroom)
const UTIL_LANE_H  = 90;
const UTIL_BAR_AREA = 70; // usable px for bars within a lane

const WEEK_MS = 7 * 86400000;

// Returns the ISO date string (YYYY-MM-DD) for the Monday of the given date's week
function weekStartOf(dateStr) {
  const d = new Date(dateStr);
  const dow = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - (dow === 0 ? 6 : dow - 1));
  return d.toISOString().slice(0, 10);
}

// Generates monthly tick timestamps within [domainMin, domainMax], thinned to ≤10
function buildMonthTicks(domainMin, domainMax) {
  const result = [];
  const d = new Date(domainMin);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  while (d.getTime() <= domainMax) {
    result.push(d.getTime());
    d.setMonth(d.getMonth() + 1);
  }
  if (result.length > 10) {
    const step = Math.ceil(result.length / 8);
    return result.filter((_, i) => i % step === 0);
  }
  return result;
}

function StatusBadge({ status }) {
  const colorClass = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {status}
    </span>
  );
}

export default function CardStatusHistory() {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConsumerCode, setSelectedConsumerCode] = useState('');
  const [cardProxyFilter, setCardProxyFilter] = useState('');
  const [sortField, setSortField] = useState('date_day');
  const [sortDir, setSortDir] = useState('desc');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryCardFilter, setSummaryCardFilter] = useState('');
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  const {
    data: consumerData,
    isLoading: consumerLoading,
    isError: consumerError,
    error: consumerErrorObj,
    isSuccess: consumerSuccess,
  } = useConsumerSearch(searchTerm);

  const consumers = consumerData?.data || [];
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const sortedConsumers = useMemo(() => {
    if (!normalizedSearchTerm) return consumers;

    const isDirectMatch = (item) => (
      [item.consumer_code, item.member_id, item.mem_nbr]
        .filter(Boolean)
        .some((value) => value.toString().trim().toLowerCase() === normalizedSearchTerm)
    );

    return [...consumers].sort((a, b) => {
      const aDirect = isDirectMatch(a);
      const bDirect = isDirectMatch(b);

      if (aDirect === bDirect) return 0;
      return aDirect ? -1 : 1;
    });
  }, [consumers, normalizedSearchTerm]);
  const consumer = useMemo(
    () => sortedConsumers.find((item) => item.consumer_code === selectedConsumerCode) || null,
    [sortedConsumers, selectedConsumerCode]
  );
  const consumerCode = consumer?.consumer_code || '';

  useEffect(() => {
    if (!sortedConsumers.length) {
      setSelectedConsumerCode('');
      return;
    }

    if (sortedConsumers.length === 1) {
      setSelectedConsumerCode(sortedConsumers[0].consumer_code);
      return;
    }

    setSelectedConsumerCode((current) => (
      sortedConsumers.some((item) => item.consumer_code === current) ? current : ''
    ));
  }, [sortedConsumers]);

  const {
    data: historyData,
    isLoading: historyLoading,
    isError: historyError,
    error: historyErrorObj,
    refetch,
    isFetching,
  } = useCardStatusHistory(consumerCode, null, null);

  const rows = historyData || [];

  const uniqueCards = useMemo(
    () => [...new Set(rows.map((r) => r.card_number_proxy))].sort(),
    [rows]
  );

  const cardLastFourMap = useMemo(
    () => Object.fromEntries(rows.filter((r) => r.card_last_four).map((r) => [r.card_number_proxy, r.card_last_four])),
    [rows]
  );

  useEffect(() => {
    if (rows.length > 0) {
      const active = rows.find((r) => r.active_card_flag === 'active')?.card_number_proxy;
      const defaultProxy = active || rows[0]?.card_number_proxy || '';
      setCardProxyFilter((current) => uniqueCards.includes(current) ? current : defaultProxy);
      setSummaryCardFilter((current) => uniqueCards.includes(current) ? current : defaultProxy);
    } else {
      setCardProxyFilter('');
      setSummaryCardFilter('');
    }
  }, [rows, uniqueCards]);

  // Shared time domain — both charts use the same values so their
  // x-axes span identically and card lanes line up vertically.
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }, []);

  const { domainMin, domainMax } = useMemo(() => {
    if (!rows.length) return { domainMin: today - 30 * 86400000, domainMax: today };
    const allTs = rows.map((r) => new Date(r.date_day).getTime());
    const minTs = Math.min(...allTs);
    const pad   = Math.max((today - minTs) * 0.02, 7 * 86400000);
    return { domainMin: minTs - pad, domainMax: today + pad };
  }, [rows, today]);

  const sortedRows = useMemo(() => {
    let filtered = rows;
    if (cardProxyFilter) {
      filtered = filtered.filter((r) => r.card_number_proxy === cardProxyFilter);
    }
    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'date_day') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortField, sortDir, cardProxyFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    if (trimmed) {
      setSearchTerm(trimmed);
      setSelectedConsumerCode('');
      setCardProxyFilter('');
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Card Status History</h1>
        <p className="text-sm text-gray-500 mt-1">
          View the full lifecycle status history of all debit cards associated with a consumer
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by consumer code, member ID, or member number..."
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={consumerLoading}
          className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {consumerLoading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {consumerError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{consumerErrorObj?.message || 'Failed to find consumer'}</p>
        </div>
      )}

      {consumerSuccess && sortedConsumers.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">No consumer found for that search term.</p>
        </div>
      )}

      {consumerSuccess && sortedConsumers.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Matching Consumers</h2>
            <p className="text-sm text-gray-500 mt-1">
              {consumer
                ? `Showing ${consumer.first_name} ${consumer.last_name}. Select a different match below to switch consumers.`
                : `Found ${sortedConsumers.length} matches for "${searchTerm}". Select the consumer you want to inspect.`}
            </p>
          </div>
          <div className="p-4 space-y-3">
            {sortedConsumers.map((item) => {
              const isSelected = item.consumer_code === selectedConsumerCode;
              return (
                <label
                  key={item.consumer_code}
                  className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="selectedConsumer"
                    value={item.consumer_code}
                    checked={isSelected}
                    onChange={(e) => {
                      setSelectedConsumerCode(e.target.value);
                      setCardProxyFilter('');
                    }}
                    className="mt-1 w-4 h-4 text-blue-600"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900">
                        {item.first_name} {item.last_name}
                      </p>
                      <p className="text-xs font-mono text-gray-500">
                        {item.consumer_code}
                      </p>
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider">Member ID</span>
                        <p className="text-sm font-mono text-gray-900">{item.member_id || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider">Member Number</span>
                        <p className="text-sm font-mono text-gray-900">{item.mem_nbr || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider">Tenant</span>
                        <p className="text-sm text-gray-900">{item.tenant_name || item.tenant_code || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {consumerSuccess && sortedConsumers.length > 1 && !consumer && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-sm">Select a consumer above to load card status history.</p>
        </div>
      )}

      {consumer && (
        <>
          {/* Consumer info banner */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Consumer</span>
                <p className="text-sm font-semibold text-gray-900">
                  {consumer.first_name} {consumer.last_name}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Consumer Code</span>
                <p className="text-sm font-mono text-gray-900">{consumer.consumer_code}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Member ID</span>
                <p className="text-sm font-mono text-gray-900">{consumer.member_id || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Tenant</span>
                <p className="text-sm text-gray-900">{consumer.tenant_name || consumer.tenant_code || 'N/A'}</p>
              </div>
            </div>
          </div>

          {historyLoading && (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-500 text-sm">Loading card status history...</span>
            </div>
          )}

          {historyError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-red-800 text-sm">{historyErrorObj?.message || 'Failed to load card status history'}</p>
              <button
                onClick={() => refetch()}
                className="mt-2 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200"
              >
                Retry
              </button>
            </div>
          )}

          {!historyLoading && !historyError && rows.length > 0 && (
            <>
              <StatusSummary
                rows={summaryCardFilter ? rows.filter((r) => r.card_number_proxy === summaryCardFilter) : rows}
                open={summaryOpen}
                onToggle={() => setSummaryOpen((o) => !o)}
                uniqueCards={uniqueCards}
                cardLastFourMap={cardLastFourMap}
                summaryCardFilter={summaryCardFilter}
                onSummaryCardChange={setSummaryCardFilter}
              />

              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <button
                  type="button"
                  onClick={() => setLifecycleOpen((o) => !o)}
                  className="w-full px-6 py-4 flex items-center gap-2 text-left hover:bg-gray-50 transition-colors"
                >
                  {lifecycleOpen ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                  <Activity className="w-5 h-5 text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">Status Lifecycle</h2>
                </button>
                {lifecycleOpen && (
                  <LifecycleChart
                    rows={rows}
                    uniqueCards={uniqueCards}
                    today={today}
                    domainMin={domainMin}
                    domainMax={domainMax}
                  />
                )}
              </div>

              <CollapsibleUtilization
                open={activityOpen}
                onToggle={() => setActivityOpen((o) => !o)}
                rows={rows}
                uniqueCards={uniqueCards}
                domainMin={domainMin}
                domainMax={domainMax}
              />
            </>
          )}

          {/* History table */}
          {!historyLoading && !historyError && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">Status Events</h2>
                  <span className="text-sm text-gray-500">({sortedRows.length} events)</span>
                </div>
                <div className="flex items-center gap-3">
                  {uniqueCards.length === 1 && (
                    <span className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-lg">
                      {cardLastFourMap[uniqueCards[0]] ? `Card ending ${cardLastFourMap[uniqueCards[0]]}` : `Card info not available (${uniqueCards[0]})`}
                    </span>
                  )}
                  {uniqueCards.length > 1 && (
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-gray-400" />
                      <select
                        value={cardProxyFilter}
                        onChange={(e) => setCardProxyFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {uniqueCards.map((proxy) => (
                          <option key={proxy} value={proxy}>
                            {cardLastFourMap[proxy] ? `Card ending ${cardLastFourMap[proxy]}` : `Card info not available (${proxy})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              {cardProxyFilter && !cardLastFourMap[cardProxyFilter] && (
                <div className="mx-6 mt-4 mb-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Card details are not available for this card. This card was never used for a transaction, so the real card number could not be retrieved.
                  </p>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader label="Date"       field="date_day"                    currentField={sortField} currentDir={sortDir} onToggle={toggleSort} />
                      <SortableHeader label="Card Proxy" field="card_number_proxy"           currentField={sortField} currentDir={sortDir} onToggle={toggleSort} />
                      <SortableHeader label="Status"     field="card_status_desc"            currentField={sortField} currentDir={sortDir} onToggle={toggleSort} />
                      <SortableHeader label="Reason"     field="status_reason_description"   currentField={sortField} currentDir={sortDir} onToggle={toggleSort} />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedRows.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-8 text-center text-gray-500 text-sm">
                          No card status events found for this consumer.
                        </td>
                      </tr>
                    ) : (
                      sortedRows.map((row, idx) => (
                        <tr key={`${row.card_number_proxy}-${row.date_day}-${row.card_status_desc}-${idx}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{formatDate(row.date_day)}</td>
                          <td className="px-4 py-3 text-sm font-mono text-gray-700 whitespace-nowrap">{row.card_number_proxy}</td>
                          <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={row.card_status_desc} /></td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.status_reason_description || <span className="text-gray-400 italic">—</span>}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Status Summary — first & most-recent dates per status
// ─────────────────────────────────────────────────────────────
function StatusSummary({ rows, open, onToggle, uniqueCards = [], cardLastFourMap = {}, summaryCardFilter = '', onSummaryCardChange }) {
  const summary = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const s = r.card_status_desc;
      const d = r.date_day;
      if (!map[s]) {
        map[s] = { first: d, latest: d };
      } else {
        if (d < map[s].first) map[s].first = d;
        if (d > map[s].latest) map[s].latest = d;
      }
    });
    return STATUS_LIFECYCLE_ORDER.map((status) => ({
      status,
      first: map[status]?.first ?? null,
      latest: map[status]?.latest ?? null,
    }));
  }, [rows]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 text-left hover:opacity-70 transition-opacity"
        >
          {open ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
          <CreditCard className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Status Summary</h2>
        </button>
        {uniqueCards.length === 1 && (
          <span className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-lg">
            {cardLastFourMap[uniqueCards[0]] ? `Card ending ${cardLastFourMap[uniqueCards[0]]}` : `Card info not available (${uniqueCards[0]})`}
          </span>
        )}
        {uniqueCards.length > 1 && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={summaryCardFilter}
              onChange={(e) => onSummaryCardChange(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {uniqueCards.map((proxy) => (
                <option key={proxy} value={proxy}>
                  {cardLastFourMap[proxy] ? `Card ending ${cardLastFourMap[proxy]}` : `Card info not available (${proxy})`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {open && (
        <div className="overflow-x-auto border-t border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Most Recent</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summary.map(({ status, first, latest }) => (
                <tr key={status} className={first ? 'hover:bg-gray-50' : 'opacity-40'}>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <StatusBadge status={status} />
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-900 whitespace-nowrap">
                    {first ? formatDate(first) : '\u2014'}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-900 whitespace-nowrap">
                    {latest ? formatDate(latest) : '\u2014'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared SVG axis renderer used by both charts below
// ─────────────────────────────────────────────────────────────
function XAxisTicks({ domainMin, domainMax, plotWidth, laneCount, laneHeight, today }) {
  const ticks = buildMonthTicks(domainMin, domainMax);
  const domainRange = domainMax - domainMin;
  const xScale = (ts) => Math.max(0, Math.min(plotWidth, ((ts - domainMin) / domainRange) * plotWidth));
  const baseY = TOP_PAD + laneCount * laneHeight;

  return (
    <g>
      {/* Baseline */}
      <line x1={LABEL_W} y1={baseY} x2={LABEL_W + plotWidth} y2={baseY} stroke="#e5e7eb" strokeWidth={1} />

      {/* Today line */}
      <line
        x1={LABEL_W + xScale(today)} y1={TOP_PAD}
        x2={LABEL_W + xScale(today)} y2={baseY}
        stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 3"
      />
      <text x={LABEL_W + xScale(today)} y={baseY + 14} textAnchor="middle" fontSize={9} fill="#94a3b8">
        today
      </text>

      {/* Month ticks */}
      {ticks.map((ts) => {
        const x = LABEL_W + xScale(ts);
        return (
          <g key={ts}>
            <line x1={x} y1={baseY} x2={x} y2={baseY + 4} stroke="#d1d5db" strokeWidth={1} />
            <text x={x} y={baseY + 16} textAnchor="middle" fontSize={9} fill="#9ca3af">
              {new Date(ts).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
            </text>
          </g>
        );
      })}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────
// Status Lifecycle — colored step segments per card lane
// ─────────────────────────────────────────────────────────────
function LifecycleChart({ rows, uniqueCards, today, domainMin, domainMax }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const domainRange = domainMax - domainMin;
  const plotWidth   = Math.max(containerWidth - LABEL_W - 24, 100);
  const xScale      = (ts) => Math.max(0, Math.min(plotWidth, ((ts - domainMin) / domainRange) * plotWidth));

  const cardIndexMap = useMemo(
    () => Object.fromEntries(uniqueCards.map((c, i) => [c, i])),
    [uniqueCards]
  );

  const TERMINAL_STATUSES = ['Replaced', 'Reported as lost or stolen'];

  const periods = useMemo(() => {
    const result = [];
    uniqueCards.forEach((card) => {
      const events = rows
        .filter((r) => r.card_number_proxy === card && !UTIL_STATUSES.includes(r.card_status_desc))
        .sort((a, b) => new Date(a.date_day) - new Date(b.date_day));
      events.forEach((ev, i) => {
        const startTs = new Date(ev.date_day).getTime();
        const nextTs  = i < events.length - 1 ? new Date(events[i + 1].date_day).getTime() : today;
        const endTs   = TERMINAL_STATUSES.includes(ev.card_status_desc) ? startTs : nextTs;
        result.push({ card, status: ev.card_status_desc, startTs, endTs, startDate: ev.date_day });
      });
    });
    return result;
  }, [rows, uniqueCards, today]);

  const statusesPresent = useMemo(
    () => [...new Set(rows.map((r) => r.card_status_desc))].filter((s) => !UTIL_STATUSES.includes(s)).sort(),
    [rows]
  );

  const svgHeight = uniqueCards.length * LANE_H + TOP_PAD + AXIS_H;

  return (
    <div className="p-4 relative" ref={containerRef}>
      <svg width={containerWidth} height={svgHeight} className="overflow-visible">
        {/* Lane backgrounds */}
        {uniqueCards.map((card, i) => (
          <rect key={card} x={LABEL_W} y={TOP_PAD + i * LANE_H} width={plotWidth} height={LANE_H}
            fill={i % 2 === 0 ? '#f9fafb' : '#ffffff'} />
        ))}

        {/* Card labels */}
        {uniqueCards.map((card, i) => (
          <text key={card} x={LABEL_W - 10} y={TOP_PAD + i * LANE_H + LANE_H / 2 + 4}
            textAnchor="end" fontSize={10} fontFamily="monospace" fill="#6b7280">
            {card}
          </text>
        ))}

        {/* Status segments */}
        {periods.map((p, idx) => {
          const cardIdx = cardIndexMap[p.card] ?? 0;
          return (
            <rect
              key={idx}
              x={LABEL_W + xScale(p.startTs)}
              y={TOP_PAD + cardIdx * LANE_H + SEG_V_PAD}
              width={Math.max(xScale(p.endTs) - xScale(p.startTs), 3)}
              height={LANE_H - SEG_V_PAD * 2}
              fill={getDotColor(p.status)}
              opacity={0.85}
              rx={3}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, period: p })}
              onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
              onMouseLeave={() => setTooltip(null)}
            />
          );
        })}

        {/* Terminal status octagon markers */}
        {periods
          .filter((p) => TERMINAL_STATUSES.includes(p.status))
          .map((p, idx) => {
            const cardIdx = cardIndexMap[p.card] ?? 0;
            const cx = LABEL_W + xScale(p.startTs);
            const cy = TOP_PAD + cardIdx * LANE_H + LANE_H / 2;
            const r = 6;
            const pts = Array.from({ length: 8 }, (_, i) => {
              const angle = (Math.PI / 8) + (i * Math.PI / 4);
              return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
            }).join(' ');
            return (
              <polygon
                key={`term-${idx}`}
                points={pts}
                fill={getDotColor(p.status)}
                stroke="#fff"
                strokeWidth={1.5}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, period: p })}
                onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                onMouseLeave={() => setTooltip(null)}
              />
            );
          })}

        <XAxisTicks
          domainMin={domainMin} domainMax={domainMax}
          plotWidth={plotWidth} laneCount={uniqueCards.length}
          laneHeight={LANE_H} today={today}
        />
      </svg>

      {tooltip && (
        <div className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm pointer-events-none space-y-0.5"
          style={{ left: tooltip.x + 14, top: tooltip.y - 48 }}>
          <p className="font-mono text-gray-400 text-xs">{tooltip.period.card}</p>
          <p className="font-semibold text-gray-900">{tooltip.period.status}</p>
          <p className="text-xs text-gray-500">
            {formatDate(tooltip.period.startDate)}
            {tooltip.period.endTs >= today
              ? ' → today'
              : ` → ${formatDate(new Date(tooltip.period.endTs))}`}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 px-1">
        {statusesPresent.map((status) => (
          <div key={status} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: getDotColor(status) }} />
            <span className="text-xs text-gray-600">{status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Transaction Activity (Weekly) — one swimlane per card,
// weekly stacked bars (Purchase, Return, Declined, Potential fraud).
// Shares the same x-axis domain as LifecycleChart above.
// ─────────────────────────────────────────────────────────────
function UtilizationChart({ rows, uniqueCards, domainMin, domainMax }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const today = useMemo(() => Date.now(), []);
  const domainRange = domainMax - domainMin;
  const plotWidth   = Math.max(containerWidth - LABEL_W - 24, 100);
  const xScale      = (ts) => Math.max(0, Math.min(plotWidth, ((ts - domainMin) / domainRange) * plotWidth));

  const weekPx = (WEEK_MS / domainRange) * plotWidth;
  const barW   = Math.max(weekPx * 0.7, 2);

  const utilRows = rows.filter((r) => UTIL_STATUSES.includes(r.card_status_desc));

  const emptyWeekBucket = () => Object.fromEntries(UTIL_STATUSES.map((s) => [s, 0]));

  const weeklyByCard = useMemo(() => {
    const data = Object.fromEntries(uniqueCards.map((c) => [c, {}]));
    utilRows.forEach((r) => {
      const card = r.card_number_proxy;
      const week = weekStartOf(r.date_day);
      if (!data[card]) return;
      if (!data[card][week]) data[card][week] = emptyWeekBucket();
      data[card][week][r.card_status_desc]++;
    });
    return data;
  }, [utilRows, uniqueCards]);

  const maxCount = useMemo(() => {
    let max = 0;
    uniqueCards.forEach((card) => {
      Object.values(weeklyByCard[card] || {}).forEach((w) => {
        const total = UTIL_STATUSES.reduce((sum, s) => sum + (w[s] || 0), 0);
        max = Math.max(max, total);
      });
    });
    return Math.max(max, 1);
  }, [weeklyByCard, uniqueCards]);

  const barH = (count) => (count / maxCount) * UTIL_BAR_AREA;

  const cardIndexMap = useMemo(
    () => Object.fromEntries(uniqueCards.map((c, i) => [c, i])),
    [uniqueCards]
  );

  const svgHeight = uniqueCards.length * UTIL_LANE_H + TOP_PAD + AXIS_H;

  if (!utilRows.length) return null;

  return (
    <div className="p-4 relative" ref={containerRef}>
      <svg width={containerWidth} height={svgHeight} className="overflow-visible">
        {/* Lane backgrounds */}
          {uniqueCards.map((card, i) => (
            <rect key={card} x={LABEL_W} y={TOP_PAD + i * UTIL_LANE_H} width={plotWidth} height={UTIL_LANE_H}
              fill={i % 2 === 0 ? '#f9fafb' : '#ffffff'} />
          ))}

          {/* Card labels + y-axis scale */}
          {uniqueCards.map((card, i) => {
            const laneTop   = TOP_PAD + i * UTIL_LANE_H;
            const laneFloor = laneTop + UTIL_LANE_H - 7;
            const midTick   = Math.round(maxCount / 2);
            return (
              <g key={card}>
                <text x={LABEL_W - 10} y={laneTop + UTIL_LANE_H / 2 + 4}
                  textAnchor="end" fontSize={10} fontFamily="monospace" fill="#6b7280">
                  {card}
                </text>
                {/* max */}
                <text x={LABEL_W + 4} y={laneFloor - UTIL_BAR_AREA + 3}
                  fontSize={8} fill="#9ca3af">{maxCount}</text>
                {/* mid */}
                {maxCount > 2 && (
                  <text x={LABEL_W + 4} y={laneFloor - UTIL_BAR_AREA / 2 + 3}
                    fontSize={8} fill="#d1d5db">{midTick}</text>
                )}
                {/* zero baseline */}
                <text x={LABEL_W + 4} y={laneFloor + 3}
                  fontSize={8} fill="#d1d5db">0</text>
              </g>
            );
          })}

          {/* Weekly stacked bars per card */}
          {uniqueCards.map((card) => {
            const cardIdx   = cardIndexMap[card] ?? 0;
            const laneFloor = TOP_PAD + (cardIdx + 1) * UTIL_LANE_H - 7; // 7px bottom padding

            return Object.entries(weeklyByCard[card] || {}).map(([week, counts]) => {
              const weekTs = new Date(week).getTime();
              const cx     = LABEL_W + xScale(weekTs + WEEK_MS / 2);
              const x      = cx - barW / 2;

              let yOffset = 0;
              const segments = UTIL_STATUSES.map((status) => {
                const h = barH(counts[status] || 0);
                const seg = { status, h, y: laneFloor - yOffset - h };
                yOffset += h;
                return seg;
              }).filter((seg) => seg.h > 0);

              return (
                <g
                  key={`${card}-${week}`}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, card, week, counts })}
                  onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                  onMouseLeave={() => setTooltip(null)}
                >
                  {segments.map((seg) => (
                    <rect key={seg.status} x={x} y={seg.y} width={barW} height={seg.h}
                      fill={getDotColor(seg.status)} opacity={0.85} rx={1} />
                  ))}
                </g>
              );
            });
          })}

          <XAxisTicks
            domainMin={domainMin} domainMax={domainMax}
            plotWidth={plotWidth} laneCount={uniqueCards.length}
            laneHeight={UTIL_LANE_H} today={today}
          />
        </svg>

        {tooltip && (
          <div className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm pointer-events-none space-y-0.5"
            style={{ left: tooltip.x + 14, top: tooltip.y - 60 }}>
            <p className="font-mono text-gray-400 text-xs">{tooltip.card}</p>
            <p className="text-gray-500 text-xs">
              {(() => {
                const s = new Date(tooltip.week);
                const e = new Date(tooltip.week);
                e.setUTCDate(e.getUTCDate() + 6);
                const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return `Week of ${fmt(s)} – ${fmt(e)}`;
              })()}
            </p>
            {UTIL_STATUSES.map((status) =>
              (tooltip.counts[status] || 0) > 0 && (
                <p key={status} className="font-medium" style={{ color: getDotColor(status) }}>
                  {status}: {tooltip.counts[status]}
                </p>
              )
            )}
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 px-1">
          {UTIL_STATUSES.map((status) => (
            <div key={status} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: getDotColor(status) }} />
              <span className="text-xs text-gray-600">{status}</span>
            </div>
          ))}
        </div>
      </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Collapsible wrapper for UtilizationChart — hidden entirely
// when no utilization-type events exist, collapsed by default
// when they do.
// ─────────────────────────────────────────────────────────────
function CollapsibleUtilization({ open, onToggle, rows, uniqueCards, domainMin, domainMax }) {
  const hasUtil = rows.some((r) => UTIL_STATUSES.includes(r.card_status_desc));
  if (!hasUtil) return null;

  const utilCount = rows.filter((r) => UTIL_STATUSES.includes(r.card_status_desc)).length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center gap-2 text-left hover:bg-gray-50 transition-colors"
      >
        {open ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
        <BarChart2 className="w-5 h-5 text-gray-500" />
        <h2 className="text-lg font-semibold text-gray-900">Transaction Activity (Weekly)</h2>
        <span className="text-sm text-gray-500">({utilCount} events)</span>
      </button>
      {open && (
        <UtilizationChart
          rows={rows}
          uniqueCards={uniqueCards}
          domainMin={domainMin}
          domainMax={domainMax}
        />
      )}
    </div>
  );
}

function SortableHeader({ label, field, currentField, currentDir, onToggle }) {
  const isActive = currentField === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
      onClick={() => onToggle(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${isActive ? 'text-blue-600' : 'text-gray-300'}`} />
        {isActive && (
          <span className="text-[10px] text-blue-600">{currentDir === 'asc' ? 'ASC' : 'DESC'}</span>
        )}
      </span>
    </th>
  );
}
